import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, MessageSquare, GraduationCap, Bell, CalendarDays, Image as ImageIcon,
  Download as DownloadIcon, BookOpen, MessageSquareWarning, Mail, CreditCard, LogOut, Plus, Trash2, Loader2, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { formatApiError } from "@/lib/utils2";
import { LOGO, ALL_CLASSES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MENU = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "enquiries", label: "Enquiries", icon: MessageSquare },
  { key: "admissions", label: "Admissions", icon: GraduationCap },
  { key: "notices", label: "Notices", icon: Bell },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "downloads", label: "Downloads", icon: DownloadIcon },
  { key: "resources", label: "Student Corner", icon: BookOpen },
  { key: "complaints", label: "Complaints", icon: MessageSquareWarning },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "payments", label: "Payments", icon: CreditCard },
];

const Field = ({ children }) => <div className="space-y-1">{children}</div>;

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const loadStats = () => api.stats().then((r) => setStats(r.data)).catch(() => {});

  const loaders = {
    enquiries: api.listEnquiries, admissions: api.listAdmissions, complaints: api.listComplaints,
    contacts: api.listContacts, payments: api.listPayments, notices: api.getNotices,
    events: api.getEvents, gallery: api.getGallery, downloads: api.getDownloads, resources: api.getStudentResources,
  };

  const loadTab = async (t) => {
    if (t === "overview") return loadStats();
    if (!loaders[t]) return;
    setLoading(true);
    try {
      const r = await loaders[t]();
      setData((d) => ({ ...d, [t]: r.data }));
    } catch (e) {
      toast.error("Failed to load data.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user) loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  if (!ready || !user) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brandred" /></div>;

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-30 inset-y-0 left-0 w-64 bg-navy text-white flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 flex items-center gap-3 border-b border-white/10">
          <img src={LOGO} alt="logo" className="h-10 w-10 object-contain bg-white rounded-full p-0.5" />
          <div><div className="font-head font-bold text-sm">Admin Panel</div><div className="text-[10px] text-gold">B.R.Smart Academy</div></div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {MENU.map((m) => (
            <button key={m.key} onClick={() => { setTab(m.key); setSidebarOpen(false); }} data-testid={`admin-tab-${m.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === m.key ? "bg-brandred text-white" : "text-slate-300 hover:bg-white/10"}`}>
              <m.icon className="h-4 w-4" /> {m.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout-btn" className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/20"><LogOut className="h-4 w-4" /> Logout</button>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="h-6 w-6 text-navy" /></button>
            <h1 className="font-head font-extrabold text-xl text-navy capitalize">{MENU.find((m) => m.key === tab)?.label}</h1>
          </div>
          <span className="text-sm text-slate-500 hidden sm:block">{user.email}</span>
        </header>

        <div className="p-5">
          {loading && <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-brandred mx-auto" /></div>}

          {tab === "overview" && <Overview stats={stats} onGo={setTab} />}
          {tab === "enquiries" && <Submissions rows={data.enquiries} columns={[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["student_class", "Class"], ["message", "Message"], ["created_at", "Date"]]} />}
          {tab === "admissions" && <Submissions rows={data.admissions} columns={[["student_name", "Student"], ["applying_class", "Class"], ["parent_name", "Parent"], ["phone", "Phone"], ["email", "Email"], ["created_at", "Date"]]} />}
          {tab === "complaints" && <Submissions rows={data.complaints} columns={[["name", "Name"], ["phone", "Phone"], ["type", "Type"], ["subject", "Subject"], ["message", "Message"], ["created_at", "Date"]]} />}
          {tab === "contacts" && <Submissions rows={data.contacts} columns={[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["subject", "Subject"], ["message", "Message"], ["created_at", "Date"]]} />}
          {tab === "payments" && <Submissions rows={data.payments} columns={[["label", "Package"], ["student_name", "Student"], ["student_class", "Class"], ["amount", "Amount (₹)"], ["payment_status", "Status"], ["created_at", "Date"]]} />}

          {tab === "notices" && <Manager t="notices" rows={data.notices} onReload={() => loadTab("notices")} fields={[
            { k: "title", label: "Title", type: "text" }, { k: "category", label: "Category", type: "select", options: ["General", "Admission", "Holiday", "Exam", "Event", "Circular"] },
            { k: "date", label: "Date", type: "date" }, { k: "content", label: "Content", type: "textarea" }]}
            add={api.addNotice} del={api.delNotice} display={(r) => <><b className="text-navy">{r.title}</b> <span className="text-xs text-brandred">[{r.category}]</span><p className="text-xs text-slate-500">{r.date}</p></>} />}

          {tab === "events" && <Manager t="events" rows={data.events} onReload={() => loadTab("events")} fields={[
            { k: "title", label: "Title", type: "text" }, { k: "date", label: "Date", type: "date" },
            { k: "description", label: "Description", type: "textarea" }, { k: "image", label: "Image URL (optional)", type: "text" }]}
            add={api.addEvent} del={api.delEvent} display={(r) => <><b className="text-navy">{r.title}</b><p className="text-xs text-slate-500">{r.date}</p></>} />}

          {tab === "gallery" && <GalleryManager rows={data.gallery} onReload={() => loadTab("gallery")} />}

          {tab === "downloads" && <Manager t="downloads" rows={data.downloads} onReload={() => loadTab("downloads")} fields={[
            { k: "title", label: "Title", type: "text" }, { k: "category", label: "Category", type: "select", options: ["Admission", "Prospectus", "Holiday", "Syllabus", "Homework", "General"] },
            { k: "url", label: "File URL", type: "text" }, { k: "description", label: "Description", type: "text" }]}
            add={api.addDownload} del={api.delDownload} display={(r) => <><b className="text-navy">{r.title}</b> <span className="text-xs text-brandred">[{r.category}]</span></>} />}

          {tab === "resources" && <Manager t="resources" rows={data.resources} onReload={() => loadTab("resources")} fields={[
            { k: "title", label: "Title", type: "text" }, { k: "category", label: "Type", type: "select", options: ["Homework", "Results", "Study Materials"] },
            { k: "target_class", label: "Class", type: "select", options: ["All", ...ALL_CLASSES] }, { k: "content", label: "Details", type: "textarea" }, { k: "url", label: "Link (optional)", type: "text" }]}
            add={api.addResource} del={api.delResource} display={(r) => <><b className="text-navy">{r.title}</b> <span className="text-xs text-brandred">[{r.category}]</span> <span className="text-xs text-slate-500">· {r.target_class}</span></>} />}
        </div>
      </div>
    </div>
  );
}

const Overview = ({ stats, onGo }) => {
  if (!stats) return null;
  const cards = [
    { key: "admissions", label: "Admissions", value: stats.admissions, color: "#E63946" },
    { key: "enquiries", label: "Enquiries", value: stats.enquiries, color: "#F4A261" },
    { key: "payments", label: "Fees Paid", value: stats.payments, color: "#2A9D8F" },
    { key: "complaints", label: "Complaints", value: stats.complaints, color: "#0A192F" },
    { key: "notices", label: "Notices", value: stats.notices, color: "#7c3aed" },
    { key: "gallery", label: "Gallery Items", value: stats.gallery, color: "#0ea5e9" },
    { key: "events", label: "Events", value: stats.events, color: "#f59e0b" },
    { key: "contacts", label: "Messages", value: stats.contacts, color: "#64748b" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <button key={c.key} onClick={() => onGo(c.key)} data-testid={`stat-${c.key}`} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-left hover:shadow-md transition-transform hover:-translate-y-0.5">
          <div className="h-2 w-10 rounded-full mb-4" style={{ backgroundColor: c.color }} />
          <div className="font-head font-black text-3xl text-navy">{c.value}</div>
          <div className="text-sm text-slate-500">{c.label}</div>
        </button>
      ))}
    </div>
  );
};

const Submissions = ({ rows, columns }) => {
  if (!rows) return null;
  if (rows.length === 0) return <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border border-slate-200">No records yet.</p>;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200"><tr>{columns.map(([, l]) => <th key={l} className="text-left px-4 py-3 font-semibold text-navy whitespace-nowrap">{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              {columns.map(([k]) => (
                <td key={k} className="px-4 py-3 text-slate-600 align-top max-w-[220px]">
                  {k === "created_at" ? String(r[k] || "").slice(0, 10) : (k === "amount" ? Number(r[k] || 0).toLocaleString("en-IN") : String(r[k] ?? "—"))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GALLERY_CATS = ["School Photos", "Annual Function", "Sports Day", "Exhibition", "Independence Day"];

const GalleryManager = ({ rows, onReload }) => {
  const [mode, setMode] = useState("upload"); // upload | url | video
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("School Photos");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setTitle(""); setImageUrl(""); setVideoUrl(""); setFile(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (mode === "upload") {
        if (!file) { toast.error("Please choose an image file."); setSaving(false); return; }
        const fd = new FormData();
        fd.append("file", file); fd.append("title", title); fd.append("category", category);
        await api.uploadGallery(fd);
      } else if (mode === "url") {
        if (!imageUrl) { toast.error("Image URL required."); setSaving(false); return; }
        await api.addGallery({ title, category, image: imageUrl, type: "photo" });
      } else {
        if (!videoUrl) { toast.error("YouTube link required."); setSaving(false); return; }
        await api.addGallery({ title, category, video_url: videoUrl, type: "video" });
      }
      toast.success("Added to gallery!");
      reset();
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delGallery(id); toast.success("Deleted."); onReload(); }
    catch { toast.error("Delete failed."); }
  };

  const modes = [{ k: "upload", l: "Upload Photo" }, { k: "url", l: "Photo URL" }, { k: "video", l: "YouTube Video" }];

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={submit} data-testid="admin-add-gallery-form" className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3 h-fit">
        <h3 className="font-head font-bold text-navy flex items-center gap-1.5"><Plus className="h-4 w-4 text-brandred" /> Add to Gallery</h3>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {modes.map((m) => (
            <button type="button" key={m.k} onClick={() => setMode(m.k)} data-testid={`gallery-mode-${m.k}`}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${mode === m.k ? "bg-white text-brandred shadow-sm" : "text-slate-500"}`}>{m.l}</button>
          ))}
        </div>
        <Field>
          <label className="text-xs font-semibold text-slate-500">Title</label>
          <Input data-testid="gallery-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field>
          <label className="text-xs font-semibold text-slate-500">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="gallery-category"><SelectValue /></SelectTrigger>
            <SelectContent>{GALLERY_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {mode === "upload" && (
          <Field>
            <label className="text-xs font-semibold text-slate-500">Image File</label>
            <Input data-testid="gallery-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Field>
        )}
        {mode === "url" && (
          <Field>
            <label className="text-xs font-semibold text-slate-500">Image URL</label>
            <Input data-testid="gallery-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
          </Field>
        )}
        {mode === "video" && (
          <Field>
            <label className="text-xs font-semibold text-slate-500">YouTube Link</label>
            <Input data-testid="gallery-video-url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
          </Field>
        )}
        <Button type="submit" disabled={saving} data-testid="admin-save-gallery-btn" className="w-full bg-brandred hover:bg-brandred/90 text-white rounded-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </form>

      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3 h-fit">
        {(!rows || rows.length === 0) && <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border border-slate-200 sm:col-span-2">No gallery items yet.</p>}
        {rows && rows.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative group">
            {r.image && <img src={mediaUrl(r.image)} alt={r.title} className="h-32 w-full object-cover" />}
            <div className="p-3">
              <b className="text-navy text-sm">{r.title}</b>
              <p className="text-xs text-brandred">{r.category} {r.type === "video" && "· Video"}</p>
            </div>
            <button onClick={() => remove(r.id)} data-testid="delete-gallery-btn" className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-white/90 text-slate-500 hover:text-brandred shadow"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Manager = ({ t, rows, fields, add, del, display, onReload }) => {
  const empty = Object.fromEntries(fields.map((f) => [f.k, ""]));
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = fields[0].k;
    if (!form[required]) { toast.error(`${fields[0].label} is required.`); return; }
    setSaving(true);
    try {
      await add(form);
      toast.success("Added successfully!");
      setForm(empty);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await del(id); toast.success("Deleted."); onReload(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={submit} data-testid={`admin-add-${t}-form`} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3 h-fit">
        <h3 className="font-head font-bold text-navy flex items-center gap-1.5"><Plus className="h-4 w-4 text-brandred" /> Add New</h3>
        {fields.map((f) => (
          <Field key={f.k}>
            <label className="text-xs font-semibold text-slate-500">{f.label}</label>
            {f.type === "textarea" ? (
              <Textarea data-testid={`field-${t}-${f.k}`} rows={3} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} />
            ) : f.type === "select" ? (
              <Select value={form[f.k]} onValueChange={(v) => set(f.k, v)}>
                <SelectTrigger data-testid={`field-${t}-${f.k}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input data-testid={`field-${t}-${f.k}`} type={f.type} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} />
            )}
          </Field>
        ))}
        <Button type="submit" disabled={saving} data-testid={`admin-save-${t}-btn`} className="w-full bg-brandred hover:bg-brandred/90 text-white rounded-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {(!rows || rows.length === 0) && <p className="text-slate-500 py-10 text-center bg-white rounded-2xl border border-slate-200">No items yet.</p>}
        {rows && rows.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-start justify-between gap-3">
            <div className="min-w-0">{display(r)}{r.content && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.content}</p>}{r.description && <p className="text-xs text-slate-500 mt-1">{r.description}</p>}</div>
            <button onClick={() => remove(r.id)} data-testid={`delete-${t}-btn`} className="text-slate-400 hover:text-brandred shrink-0"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
